/**
 * StaffList — verbatim port of StaffLibrary + StaffGroups (two-tab screen).
 * Route: staff (tab: Staff members) · staff.groups (tab: Groups)
 *
 * DS-convergence pass (2026-08-10): the two hand-rolled <table>s are now the
 * shared jrni-ui `Table` (rows gain keyboard reach + a focus/hover treatment
 * they never had), the local `FilterDropdown` fork is the shared `FilterSelect`
 * it was promoted from, the status pill rows are `ChipToggleGroup`, the staff
 * glyph is `Avatar`, and both tabs route their read through `LoadState` so a
 * failed list read can no longer render as "no staff".
 */
import { useState, type MouseEvent, type ReactNode } from "react";
import { Avatar, Btn, Card, ChipToggleGroup, Divider, FilterSelect, Icon, Input, Pill, T, Table, type TableColumn } from "@v0y4ge/jrni-ui";
import { Screen, PageHeader } from "../../shell/chrome";
import { FilterBar, EmptyState, ExplainerFooter, LoadState, RowActions } from "../../shell/composites";
import { useViewMode, ViewToggle, CardActionBar, CARD_GRID } from "../../shell/list-composites";
import { StatTile } from "../../shell/composites";
import { can } from "../../shell/access";
import { toast } from "../../shell/toast";
import { useNav } from "../../shell/router";
import { useStaff, type StaffMember, type StaffGroup } from "./useStaff";
import { SVC_CHANNEL, SVC_CHANNELS } from "../../lib/svc-editor-primitives";
import { useT, t as i18nT } from "../../lib/i18n";

/* ── Constants ─────────────────────────────────────────────────────────── */
const STAFF_STATUSES: Record<string, { label: string; labelKey: string; tone: "success" | "neutral" | "warn" }> = {
  active:     { label: "Active", labelKey: "common.status.active", tone: "success" },
  inactive:   { label: "Inactive", labelKey: "common.status.inactive", tone: "neutral" },
  "on-leave": { label: "On leave", labelKey: "staff.list.onLeave", tone: "warn" },
};

/* ── Helpers ────────────────────────────────────────────────────────────── */
export function staffName(m: StaffMember) { return [m.firstName, m.lastName].filter(Boolean).join(" ") || m.email || m.id; }
export function staffInitials(m: StaffMember) {
  const f = m.firstName?.[0] ?? ""; const l = m.lastName?.[0] ?? "";
  return (f + l).toUpperCase() || (m.email?.[0] ?? "?").toUpperCase();
}

function StaffStatusPill({ status }: { status: string }) {
  const t = useT();
  const s = STAFF_STATUSES[status] ?? STAFF_STATUSES.active;
  return <Pill tone={s.tone} dot>{t(s.labelKey, s.label)}</Pill>;
}
function GroupStatusPill({ status }: { status: string }) {
  const t = useT();
  return status === "published" ? <Pill tone="success" dot>{t("common.status.published", "Published")}</Pill> : <Pill tone="warn" dot>{t("common.status.draft", "Draft")}</Pill>;
}
/** The staff entity glyph — jrni-ui `Avatar` (rounded-square), which also owns
 *  the broken-image fallback the hand-rolled <img> did not have. */
function StaffAvatar({ m, size = 32 }: { m: StaffMember; size?: number }) {
  return <Avatar shape="square" size={size} src={m.photo} initials={staffInitials(m)} color={m.color ?? T.p1} alt={staffName(m)} />;
}
function ChannelGlyphs({ channels }: { channels?: string[] }) {
  const t = useT();
  if (!channels?.length) return <span style={{ fontSize: T.text.label.fontSize, color: T.soft }}>{t("staff.list.notBookable", "Not bookable")}</span>;
  return <div style={{ display: "flex", gap: T.space[2] }}>{channels.map((id) => { const c = SVC_CHANNEL(id); const G = (Icon as Record<string, (p: { size: number }) => ReactNode>)[c.icon] ?? Icon.calendar; return <span key={id} title={c.label} style={{ display: "inline-flex" }}><G size={15} /></span>; })}</div>;
}
function sectionTabs(nav: ReturnType<typeof useNav>, store: ReturnType<typeof useStaff>) {
  return [
    { id: "staff",  label: i18nT("staff.list.staff", "Staff"),  count: store.members.length, onClick: () => nav.go("staff") },
    { id: "groups", label: i18nT("staff.list.groups", "Groups"), count: store.groups.length,  onClick: () => nav.go("staff.groups") },
  ];
}
/** The row count line at the far end of the filter bar (both tabs). */
function FilterCount({ shown, total }: { shown: number; total: number }) {
  const t = useT();
  return <span style={{ marginLeft: "auto", fontSize: T.text.label.fontSize, color: T.soft }}>{shown} {t("staff.list.of", "of")}{" "}{total}</span>;
}
/** Thin rule between filter-bar clusters. */
const FilterRule = () => <Divider orientation="vertical" inset={4} style={{ height: 20, alignSelf: "center" }} />;

/* ═══ Tab 1: Staff members ══════════════════════════════════════════════ */
export function StaffLibrary() {
  const t = useT();
  const statusLabel = (status: string) => { const meta = STAFF_STATUSES[status]; return meta ? t(meta.labelKey, meta.label) : status; };
  const nav = useNav();
  const store = useStaff();
  const [view, setView] = useViewMode("staff:view");
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [chanFilter, setChanFilter] = useState<string | null>(null);
  const [groupFilter, setGroupFilter] = useState<string | null>(null);
  const canWrite = can("team.write");

  const members = store.members;
  const groups  = store.groups;
  const rows = members.filter((m) => {
    if (statusFilter !== "all" && m.status !== statusFilter) return false;
    if (chanFilter && !(m.channels ?? []).includes(chanFilter as never)) return false;
    if (groupFilter && !(m.groups ?? []).includes(groupFilter)) return false;
    if (query.trim() && !`${staffName(m)} ${m.email} ${m.title} ${m.id}`.toLowerCase().includes(query.toLowerCase())) return false;
    return true;
  });
  const active   = members.filter((m) => m.status === "active").length;
  const bookable = members.filter((m) => (m.channels ?? []).length > 0).length;

  const dup = async (m: StaffMember) => { const c = { ...m, id: "STAFF-" + Date.now(), status: "inactive" as const, firstName: m.firstName + " (copy)" }; try { await store.upsertMember(c); toast({ tone: "success", title: t("staff.list.staffDuplicated", "Staff duplicated") }); } catch (e) { console.warn("duplicate staff failed:", e); toast({ tone: "danger", title: t("staff.list.couldnTDuplicateStaff", "Couldn't duplicate staff member"), subtitle: t("staff.list.retryInAMoment", "Retry in a moment.") }); } };
  const del = async (m: StaffMember) => { try { await store.removeMember(m.id); toast({ tone: "warn", title: t("staff.list.staffMemberRemoved", "Staff member removed") }); } catch (e) { console.warn("remove staff failed:", e); toast({ tone: "danger", title: t("staff.list.couldnTRemoveStaff", "Couldn't remove staff member"), subtitle: t("staff.list.retryInAMoment", "Retry in a moment.") }); } };
  const setStatus = async (m: StaffMember, s: string) => { try { await store.upsertMember({ ...m, status: s as StaffMember["status"] }); toast({ tone: s === "active" ? "success" : "warn", title: i18nT("staff.list.markedStaffStatusesSLabelTolowercase.icu", { value0: statusLabel(s).toLowerCase() }) }); } catch (e) { console.warn("update staff status failed:", e); toast({ tone: "danger", title: t("staff.list.couldnTUpdateStatus", "Couldn't update status"), subtitle: t("staff.list.retryInAMoment", "Retry in a moment.") }); } };
  const groupNames = (m: StaffMember) => (m.groups ?? []).map((g: string) => groups.find((x) => x.id === g)?.name).filter(Boolean) as string[];
  const anyFilter = statusFilter !== "all" || chanFilter || groupFilter || query.trim();
  const borderColor = (m: StaffMember) => m.status === "active" ? T.green1 : m.status === "on-leave" ? T.orange1 : T.n4;

  const mkActions = (m: StaffMember) => ({
    onView: () => nav.go("staff.edit", { id: m.id, view: 1 }),
    viewLabel: "Preview",
    onEdit: canWrite ? () => nav.go("staff.edit", { id: m.id }) : undefined,
    onDuplicate: canWrite ? () => dup(m) : undefined,
    moreHeader: staffName(m),
    moreItems: canWrite ? [
      ...(m.status !== "active"    ? [{ icon: <Icon.check size={14} />, label: t("staff.list.markActive", "Mark active"),   onClick: () => setStatus(m, "active") }]    : []),
      ...(m.status !== "on-leave"  ? [{ icon: <Icon.clock size={14} />, label: t("staff.list.markOnLeave", "Mark on leave"), onClick: () => setStatus(m, "on-leave") }]  : []),
      ...(m.status !== "inactive"  ? [{ icon: <Icon.refresh size={14}/>, label: t("staff.list.markInactive", "Mark inactive"), onClick: () => setStatus(m, "inactive") }] : []),
      "divider" as const,
      { icon: <Icon.trash size={14} />, label: t("common.actions.remove", "Remove"), danger: true, onClick: () => del(m) },
    ] : [],
  });

  /* Status filter — the shared pill-toggle group (was four hand-rolled pill
     Btns whose active state paired an inverting `T.p1` fill with fixed
     `T.white` ink). `staff.list.all` is a suggested key pending a catalog
     entry; the fallback renders the shipped English verbatim until it lands. */
  const statusOptions = [
    { value: "all",      label: t("staff.list.all", "All"),                     count: members.length },
    { value: "active",   label: t("common.status.active", "Active"),            count: active },
    { value: "on-leave", label: t("staff.list.onLeave", "On leave") },
    { value: "inactive", label: t("common.status.inactive", "Inactive") },
  ];

  /* Table columns (DS-convergence 2026-08-10) — the hand-rolled <table> is now
     the shared jrni-ui Table; row click/hover/keyboard activation and the
     per-row status accent are the primitive's. Actions stops row-nav. */
  const columns: TableColumn<StaffMember>[] = [
    { id: "name", header: t("staff.list.name", "Name"), cell: (m) => (
      <div style={{ display: "flex", alignItems: "center", gap: T.space[2] }}>
        <StaffAvatar m={m} size={32} />
        <div>
          <div style={{ color: T.n1, fontWeight: 600 }}>{staffName(m)}</div>
          <div style={{ color: T.soft, fontSize: T.text.label.fontSize }}>{m.title || m.id}</div>
        </div>
      </div>
    ) },
    { id: "role", header: t("staff.list.roleSignIn", "Role / sign-in"), cell: (m) => (
      m.signsIn
        ? <span style={{ display: "inline-flex", alignItems: "center", gap: T.space[2], color: T.p1, fontWeight: 600 }}><Icon.user size={13} /> {m.role}</span>
        : <span style={{ color: T.soft, display: "inline-flex", alignItems: "center", gap: T.space[2] }}><Icon.lock size={12} /> {t("staff.list.noSignIn", "No sign-in")}</span>
    ) },
    { id: "channels", header: t("staff.list.channels", "Channels"), cell: (m) => <ChannelGlyphs channels={m.channels} /> },
    { id: "groups", header: t("staff.list.groups", "Groups"), cell: (m) => <span style={{ color: T.n2 }}>{groupNames(m).join(", ") || "—"}</span> },
    { id: "locations", header: t("staff.list.locations", "Locations"), cell: (m) => <span style={{ color: (m.locations ?? []).length ? T.n1 : T.soft }}>{(m.locations ?? []).length || "—"}</span> },
    { id: "status", header: t("staff.list.status", "Status"), cell: (m) => <StaffStatusPill status={m.status} /> },
    { id: "actions", header: t("staff.list.actions", "Actions"), align: "right", minWidth: 130, cell: (m) => (
      <span style={{ display: "inline-flex" }} onClick={(e: MouseEvent<HTMLSpanElement>) => e.stopPropagation()}>
        <RowActions {...mkActions(m)} />
      </span>
    ) },
  ];

  return (
    <Screen rail="staff" tabs={sectionTabs(nav, store)} activeTab="staff">
      <PageHeader title={t("staff.list.staff", "Staff")}
        subtitle={t("staff.list.thePeopleBookableToDeliverServicesThroughAppointmentsEventsAndQueuesIncl", "The people bookable to deliver services through appointments, events and queues — including those who never sign in.")}
        breadcrumb={null}
        actions={<>
          <ViewToggle mode={view} onChange={setView} />
          {canWrite ? <Btn icon={<Icon.plus />} onClick={() => nav.go("staff.edit", { new: 1 })}>{t("staff.list.newStaffMember", "New staff member")}</Btn> : null}
        </>}
      />
      <div style={{ padding: `${T.space[4]} ${T.space[6]} 0`, display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: T.space[3] }}>
        <StatTile label={t("staff.list.staff", "Staff")}    value={members.length} icon={<Icon.users size={14} />}    accent={T.p1} />
        <StatTile label={t("common.status.active", "Active")}   value={active}         icon={<Icon.check size={14} />}    accent={T.green1} />
        <StatTile label={t("staff.list.bookable", "Bookable")} value={bookable}       icon={<Icon.calendar size={14} />} accent={T.tDarkBlue} />
        <StatTile label={t("staff.list.groups", "Groups")}   value={groups.length}  icon={<Icon.tags size={14} />}     accent={T.p1} />
      </div>
      <FilterBar>
        <Input full={false} leading={<Icon.search size={14} />} placeholder={t("staff.list.searchNameEmailId", "Search name, email, ID…")} value={query} onChange={(e) => setQuery(e.target.value)} size="sm" style={{ width: 240 }} />
        <FilterRule />
        <ChipToggleGroup ariaLabel={t("staff.list.status", "Status")} options={statusOptions} value={statusFilter} onChange={(v) => setStatusFilter(v as string)} />
        <FilterRule />
        <FilterSelect label={chanFilter ? SVC_CHANNEL(chanFilter).label : t("staff.staffList.anyChannel", "Any channel")} active={!!chanFilter} anyLabel={t("staff.staffList.anyChannel", "Any channel")} options={SVC_CHANNELS.map((c) => c.label)} onPick={(lbl) => setChanFilter(SVC_CHANNELS.find((c) => c.label === lbl)?.id ?? null)} onClear={() => setChanFilter(null)} />
        <FilterSelect label={groupFilter ? (groups.find((g) => g.id === groupFilter)?.name ?? t("staff.staffList.anyGroup", "Any group")) : t("staff.staffList.anyGroup", "Any group")} active={!!groupFilter} anyLabel={t("staff.staffList.anyGroup", "Any group")} options={groups.map((g) => g.name)} onPick={(name) => setGroupFilter(groups.find((g) => g.name === name)?.id ?? null)} onClear={() => setGroupFilter(null)} />
        {anyFilter ? <Btn variant="link" size="sm" onClick={() => { setStatusFilter("all"); setChanFilter(null); setGroupFilter(null); setQuery(""); }}>{t("staff.list.clearAll", "Clear all")}</Btn> : null}
        <FilterCount shown={rows.length} total={members.length} />
      </FilterBar>

      <div style={{ flex: 1, overflow: "auto", padding: `0 ${T.space[6]} ${T.space[6]}` }}>
        <LoadState
          loading={store.loading}
          error={store.error}
          empty={rows.length === 0}
          onRetry={() => store.reload()}
          emptyState={
            <EmptyState variant="card" icon={<Icon.users size={20} />} title={t("staff.list.noStaffMatch", "No staff match")} body={t("staff.list.tryClearingAFilterOrAddAStaffMember", "Try clearing a filter, or add a staff member.")}
              action={canWrite ? <Btn icon={<Icon.plus size={14} />} onClick={() => nav.go("staff.edit", { new: 1 })}>{t("staff.list.newStaffMember", "New staff member")}</Btn> : null} />
          }
        >
          {view === "grid" ? (
            <div style={{ display: "grid", gridTemplateColumns: CARD_GRID, gap: T.space[4] }}>
              {rows.map((m) => (
                <Card key={m.id} onClick={() => mkActions(m).onView()} style={{ padding: 0, cursor: "pointer", overflow: "hidden", display: "flex", flexDirection: "column", borderLeft: `3px solid ${borderColor(m)}` }}>
                  <div style={{ padding: T.space[4], flex: 1, display: "flex", flexDirection: "column", gap: T.space[3] }}>
                    <div style={{ display: "flex", alignItems: "flex-start", gap: T.space[2] }}>
                      <StaffAvatar m={m} size={42} />
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <div style={{ fontSize: T.text.h3.fontSize, fontWeight: 700, color: T.n1 }}>{staffName(m)}</div>
                        <div style={{ fontSize: T.text.bodySm.fontSize, color: T.soft, marginTop: 1 /* optical */ }}>{m.title || (m.role ?? "Bookable staff")}</div>
                      </div>
                      <StaffStatusPill status={m.status} />
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: T.space[2], flexWrap: "wrap" }}>
                      <ChannelGlyphs channels={m.channels} />
                      {m.signsIn ? <span style={{ display: "inline-flex", alignItems: "center", gap: T.space[1], fontSize: T.text.label.fontSize, fontWeight: 600, color: T.p1 }}><Icon.user size={12} /> {m.role}</span> : <span style={{ fontSize: T.text.label.fontSize, color: T.soft, display: "inline-flex", alignItems: "center", gap: T.space[1] }}><Icon.lock size={11} /> {t("staff.list.noSignIn", "No sign-in")}</span>}
                    </div>
                    {groupNames(m).length ? <div style={{ display: "flex", flexWrap: "wrap", gap: T.space[2] }}>{groupNames(m).map((n) => <Pill key={n} tone="neutral">{n}</Pill>)}</div> : null}
                    <div style={{ marginTop: "auto", paddingTop: T.space[3], borderTop: `1px solid ${T.border}`, fontSize: T.text.label.fontSize, color: T.soft, display: "flex", gap: T.space[3] }}>
                      <span style={{ display: "inline-flex", alignItems: "center", gap: T.space[2] }}><Icon.loc size={12} /> {(m.locations ?? []).length} {t("staff.list.location", "location")}{(m.locations ?? []).length === 1 ? "" : t('staff.list.text')}</span>
                    </div>
                  </div>
                  <CardActionBar {...mkActions(m)} />
                </Card>
              ))}
            </div>
          ) : (
            <Card style={{ padding: 0 }}>
              <Table<StaffMember>
                columns={columns}
                rows={rows}
                getRowId={(m) => m.id}
                onRowClick={(m) => mkActions(m).onView()}
                rowAccent={borderColor}
                style={{ border: "none", borderRadius: T.radius.none }}
              />
            </Card>
          )}
        </LoadState>
        <ExplainerFooter>{t("staff.list.a", "A")}{" "}<b>{t("staff.list.staffMember", "staff member")}</b> {t("staff.list.isTheSameObjectAsAUserSomeSignInAndCarry", "is the same object as a user — some sign in (and carry a Studio role), others never sign in but are still bookable. Authorization comes from their")}{" "}<b>{t("staff.list.groups4ed379d4", "groups")}</b> {t("staff.list.plusIndividualGrants", "plus individual grants.")}</ExplainerFooter>
      </div>
    </Screen>
  );
}

/* ═══ Tab 2: Staff groups ═══════════════════════════════════════════════ */
export function StaffGroups() {
  const t = useT();
  const nav = useNav();
  const store = useStaff();
  const [view, setView] = useViewMode("staffgroups:view");
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const canWrite = can("team.write");

  const all = store.groups;
  const rows = all.filter((g) => {
    if (statusFilter !== "all" && g.status !== statusFilter) return false;
    if (query.trim() && !g.name.toLowerCase().includes(query.toLowerCase())) return false;
    return true;
  });
  const published = all.filter((g) => g.status === "published").length;
  const countInGroup = (gid: string) => store.members.filter((m) => (m.groups ?? []).includes(gid)).length;
  const dup = async (g: StaffGroup) => { try { await store.upsertGroup({ ...g, id: "grp_" + Date.now(), name: g.name + " (copy)", status: "draft" }); toast({ tone: "success", title: t("staff.list.groupDuplicated", "Group duplicated") }); } catch (e) { console.warn("duplicate staff group failed:", e); toast({ tone: "danger", title: t("staff.list.couldnTDuplicateGroup", "Couldn't duplicate group"), subtitle: t("staff.list.retryInAMoment", "Retry in a moment.") }); } };
  const del = async (g: StaffGroup) => { try { await store.removeGroup(g.id); toast({ tone: "warn", title: t("staff.list.groupDeleted", "Group deleted") }); } catch (e) { console.warn("delete staff group failed:", e); toast({ tone: "danger", title: t("staff.list.couldnTDeleteGroup", "Couldn't delete group"), subtitle: t("staff.list.retryInAMoment", "Retry in a moment.") }); } };
  const toggle = async (g: StaffGroup) => { const next = g.status === "published" ? "draft" : "published"; try { await store.upsertGroup({ ...g, status: next }); toast({ tone: next === "published" ? "success" : "warn", title: next === "published" ? t("staff.list.groupPublished", "Group published") : t("common.status.movedToDraft", "Moved to draft") }); } catch (e) { console.warn("toggle staff group status failed:", e); toast({ tone: "danger", title: t("staff.list.couldnTUpdateGroup", "Couldn't update group"), subtitle: t("staff.list.retryInAMoment", "Retry in a moment.") }); } };

  const mkActions = (g: StaffGroup) => ({
    onView: () => nav.go("staff.group.edit", { id: g.id, view: 1 }),
    viewLabel: "Preview",
    onEdit: canWrite ? () => nav.go("staff.group.edit", { id: g.id }) : undefined,
    onDuplicate: canWrite ? () => dup(g) : undefined,
    moreHeader: g.name,
    moreItems: canWrite ? [
      g.status === "published" ? { icon: <Icon.refresh size={14} />, label: t("staff.list.moveToDraft", "Move to draft"), onClick: () => toggle(g) } : { icon: <Icon.check size={14} />, label: t("common.actions.publish", "Publish"), onClick: () => toggle(g) },
      "divider" as const,
      { icon: <Icon.trash size={14} />, label: t("staff.list.deleteGroup", "Delete group"), danger: true, onClick: () => del(g) },
    ] : [],
  });

  /* `staff.list.all` / `staff.list.drafts` are suggested keys pending catalog
     entries; the fallbacks render the shipped English verbatim until they land. */
  const statusOptions = [
    { value: "all",       label: t("staff.list.all", "All"),                      count: all.length },
    { value: "published", label: t("common.status.published", "Published"),       count: published },
    { value: "draft",     label: t("staff.list.drafts", "Drafts"),                count: all.length - published },
  ];

  const columns: TableColumn<StaffGroup>[] = [
    { id: "group", header: t("staff.list.group", "Group"), cell: (g) => (
      <div style={{ display: "flex", alignItems: "center", gap: T.space[2] }}>
        {/* The group's own hue is DATA (an arbitrary API colour), and no jrni-ui
            primitive renders a bare 14px colour swatch — kept as a plain span
            (not a raw form/table primitive, so no `ui-raw` marker applies). */}
        <span style={{ width: 14, height: 14, borderRadius: T.radius.sm, background: g.hue ?? T.p1, flexShrink: 0 }} />
        <div>
          <div style={{ color: T.n1, fontWeight: 600 }}>{g.name}</div>
          <div style={{ color: T.soft, fontSize: T.text.label.fontSize }}>{g.description}</div>
        </div>
      </div>
    ) },
    { id: "members", header: t("staff.list.members", "Members"), cell: (g) => <span style={{ color: T.n1 }}>{countInGroup(g.id)}</span> },
    { id: "status", header: t("staff.list.status", "Status"), cell: (g) => <GroupStatusPill status={g.status} /> },
    { id: "actions", header: t("staff.list.actions", "Actions"), align: "right", minWidth: 130, cell: (g) => (
      <span style={{ display: "inline-flex" }} onClick={(e: MouseEvent<HTMLSpanElement>) => e.stopPropagation()}>
        <RowActions {...mkActions(g)} />
      </span>
    ) },
  ];

  return (
    <Screen rail="staff" tabs={sectionTabs(nav, store)} activeTab="groups">
      <PageHeader title={t("staff.list.staffGroups", "Staff groups")} subtitle={t("staff.list.groupStaffByFunctionAGroupSupportsASetOfServicesEvery", "Group staff by function. A group supports a set of services — every member inherits authorization to deliver them.")} breadcrumb={null}
        actions={<>
          <ViewToggle mode={view} onChange={setView} />
          {canWrite ? <Btn icon={<Icon.plus />} onClick={() => nav.go("staff.group.edit", { new: 1 })}>{t("staff.list.newGroup", "New group")}</Btn> : null}
        </>}
      />
      <FilterBar>
        <Input full={false} leading={<Icon.search size={14} />} placeholder={t("staff.list.searchGroups", "Search groups…")} value={query} onChange={(e) => setQuery(e.target.value)} size="sm" style={{ width: 240 }} />
        <FilterRule />
        <ChipToggleGroup ariaLabel={t("staff.list.status", "Status")} options={statusOptions} value={statusFilter} onChange={(v) => setStatusFilter(v as string)} />
        <FilterCount shown={rows.length} total={all.length} />
      </FilterBar>
      <div style={{ flex: 1, overflow: "auto", padding: `0 ${T.space[6]} ${T.space[6]}` }}>
        <LoadState
          loading={store.loading}
          error={store.error}
          empty={rows.length === 0}
          onRetry={() => store.reload()}
          emptyState={
            <EmptyState variant="card" icon={<Icon.tags size={20} />} title={t("staff.list.noGroupsMatch", "No groups match")} body={t("staff.list.tryADifferentSearchOrCreateANewGroup", "Try a different search, or create a new group.")}
              action={canWrite ? <Btn icon={<Icon.plus size={14} />} onClick={() => nav.go("staff.group.edit", { new: 1 })}>{t("staff.list.newGroup", "New group")}</Btn> : null} />
          }
        >
          {view === "grid" ? (
            <div style={{ display: "grid", gridTemplateColumns: CARD_GRID, gap: T.space[4] }}>
              {rows.map((g) => {
                const count = countInGroup(g.id);
                return (
                  <Card key={g.id} onClick={() => mkActions(g).onView()} style={{ padding: 0, cursor: "pointer", overflow: "hidden", display: "flex", flexDirection: "column", borderTop: `4px solid ${g.hue}` }}>
                    <div style={{ padding: T.space[4], flex: 1, display: "flex", flexDirection: "column", gap: T.space[3] }}>
                      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: T.space[2] }}>
                        <div style={{ minWidth: 0 }}><div style={{ fontSize: T.text.h3.fontSize, fontWeight: 700, color: T.n1 }}>{g.name || "Untitled"}</div><div style={{ fontSize: T.text.bodySm.fontSize, color: T.soft, marginTop: T.space[1], lineHeight: 1.4 }}>{g.description || "No description"}</div></div>
                        <GroupStatusPill status={g.status} />
                      </div>
                      <div style={{ marginTop: "auto", paddingTop: T.space[3], borderTop: `1px solid ${T.border}`, fontSize: T.text.label.fontSize, color: T.soft, display: "flex", gap: T.space[3] }}>
                        <span style={{ display: "inline-flex", alignItems: "center", gap: T.space[2] }}><Icon.users size={12} /> {count} {t("staff.list.member", "member")}{" "}{count === 1 ? "" : t('staff.list.text')}</span>
                        <span style={{ display: "inline-flex", alignItems: "center", gap: T.space[2] }}><Icon.layers size={12} /> {(g.services ?? []).length} {t("staff.list.service", "service")}{(g.services ?? []).length === 1 ? "" : t('staff.list.text')}</span>
                      </div>
                    </div>
                    <CardActionBar {...mkActions(g)} />
                  </Card>
                );
              })}
            </div>
          ) : (
            <Card style={{ padding: 0 }}>
              <Table<StaffGroup>
                columns={columns}
                rows={rows}
                getRowId={(g) => g.id}
                onRowClick={(g) => mkActions(g).onView()}
                style={{ border: "none", borderRadius: T.radius.none }}
              />
            </Card>
          )}
        </LoadState>
        <ExplainerFooter>{t("staff.list.servicesAre", "Services are")}{" "}<b>{t("staff.list.assignedToGroups", "assigned to groups")}</b> {t("staff.list.everyMemberOfAGroupCanDeliverTheGroupSServicesIndividuals", "— every member of a group can deliver the group's services. Individuals can be granted extra services or excluded from specific ones in their profile.")}</ExplainerFooter>
      </div>
    </Screen>
  );
}
